import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    q: "How does the AI Photo Detector work?",
    a: "Our engine uses a multi-layered approach: analyzing neural noise patterns, checking for common generative artifacts (like GAN-specific pixel frequencies), and auditing EXIF metadata for signatures left by platforms like Midjourney or DALL-E."
  },
  {
    q: "Can it distinguish between AI-edited and fully AI-generated photos?",
    a: "Yes. By analyzing the 'Veracity Score' and Forensic Rules, you can see if an image has 'Moderate' risk (likely edited via AI tools) vs 'Very High' risk (likely fully synthesized)."
  },
  {
    q: "Is detection 100% accurate?",
    a: "AI detection is a probabilistic science. While our heuristics are highly effective against current generative models, we provide a 'Confidence Score' to indicate the reliability of the specific analysis."
  },
  {
    q: "What are AI Signatures in metadata?",
    a: "Many AI tools omit standard camera metadata (Make/Model) or inject specific software tags. We flag these inconsistencies as part of our forensic audit."
  },
  {
    q: "Does it detect 'Deepfakes'?",
    a: "Yes, it can detect facial artifacts and frequency anomalies common in deepfake synthesis, though it is optimized for static images rather than video."
  },
  {
    q: "Why does it require local processing?",
    a: "To ensure 100% privacy and high speed. By running neural analysis in your browser, your sensitive evidence never leaves your device, preventing leaks."
  },
  {
    q: "Can it identify the specific AI model used?",
    a: "In many cases, yes. Our software attribution engine looks for specific 'CreatorTool' and 'ProcessingSoftware' tags associated with Adobe Firefly, Stable Diffusion, and others."
  },
  {
    q: "How do I interpret a 60% Risk Score?",
    a: "A 60% score (High Risk) suggests significant metadata anomalies or neural patterns that do not match typical digital camera sensors, indicating a likely synthetic origin."
  },
  {
    q: "Is this tool free to use?",
    a: "The browser-based Investigator tool is free and powered by TakedownTaskforce.com as part of our commitment to digital veracity."
  },
  {
    q: "Can I use the PDF report in legal proceedings?",
    a: "The report is a forensic advisory tool. For formal legal cases, we recommend contacting Takedown Taskforce for a certified expert witness analysis."
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-16 border-t border-slate-800" aria-label="Frequently Asked Questions" itemScope itemType="https://schema.org/FAQPage">
      <div className="max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-center mb-10 text-slate-100">AI Detection FAQ</h3>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/50"
              itemScope 
              itemProp="mainEntity" 
              itemType="https://schema.org/Question"
            >
              <button 
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-800/50 transition"
                aria-expanded={openIndex === i}
              >
                <span className="font-semibold text-slate-200" itemProp="name">{faq.q}</span>
                <SafeIcon icon={openIndex === i ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-slate-500" />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                    itemScope 
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <div className="px-6 pb-4 text-sm text-slate-400 leading-relaxed border-t border-slate-800/50 pt-4" itemProp="text">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;